// ABOUTME: Displays a candidate profile modal from a Kanban column.
// ABOUTME: Keeps candidate navigation stable while the live column changes.
import React from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "@material-ui/core/Dialog";
import { useTranslation } from "react-i18next";
import ApplicantView from "./ApplicantView";
import theme from "theme/Theme";
import { useMediaQuery } from "@material-ui/core";
import { useHotkeys } from "react-hotkeys-hook";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import CloseIcon from "@material-ui/icons/Close";
import {
    ApplicantViewJobDataInsideJob,
    ApplicantViewModulesType
} from "./useApplicantViewContext/ApplicantViewTypes";
import { IOrderData } from "Pages/Job/JobView/useJobContext/JobViewTypes";
import { apiDirectCall } from "customHooks/useApi/api";
import {
    createApplicantNavigationSnapshot,
    createApplicantNavigationVersion,
    findApplicantNavigationPosition,
    getLoadedApplicantId,
    hydrateApplicantNavigationSnapshot,
    resolveApplicantNavigationState
} from "./applicantModalNavigation";
import { findApplicantIndex } from "Pages/Job/JobView/JobKanban/applicantStageReadConsistency";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paperDialogProps: {
            backgroundColor: theme.palette.backgroundGrey.main,
            minHeight: "95vh",
            maxHeight: "95vh"
        },
        dialogActions: {
            backgroundColor: "white",
            borderTopColor: theme.palette.backgroundGrey.main,
            borderTopWidth: 2,
            borderTopStyle: "solid"
        }
    })
);

export interface IApplicantViewModalContainerNavigationInfoKanban {
    index: number;
    fieldArray: Compleo.IObject[];
    orderData: IOrderData[];
    reactWindowRef: any;
    stageId: string;
}

interface IPropsItemFilter {
    open: boolean;
    onClose: () => void;
    navigationInfo: IApplicantViewModalContainerNavigationInfoKanban;
    jobDataInsideJob: ApplicantViewJobDataInsideJob;
    module: ApplicantViewModulesType;
}

const ApplicantViewModalContainerKanban = (props: IPropsItemFilter) => {
    const { onClose, open, navigationInfo, jobDataInsideJob, module } = props;
    const classes = useStyles();
    const moduleName = module;

    const [navigationSnapshot, setNavigationSnapshot] = React.useState(() =>
        createApplicantNavigationSnapshot(
            navigationInfo?.fieldArray || [],
            navigationInfo?.index
        )
    );
    const navigationVersion = createApplicantNavigationVersion(
        navigationInfo.orderData,
        navigationInfo.stageId
    );
    const [openingNavigationVersion] = React.useState(navigationVersion);
    const canHydrateUnresolvedApplicants =
        navigationVersion === openingNavigationVersion;
    const [navigationState, setNavigationState] = React.useState({
        currentPosition: navigationSnapshot.initialPosition,
        pendingPosition: null as number | null
    });
    const { currentPosition, pendingPosition } = navigationState;
    const [externalDataLoadFinished, setExternalDataLoadFinished] =
        React.useState(false);
    const [customCVsData, setCustomCVsData] = React.useState<Compleo.IObject[]>(
        []
    );
    const currentApplicant = navigationSnapshot.applicants[currentPosition];
    const localApplicantId = currentApplicant?.applicantId;
    const loadedApplicantId = getLoadedApplicantId(
        currentApplicant,
        navigationInfo.fieldArray,
        canHydrateUnresolvedApplicants
    );
    const pendingApplicant =
        pendingPosition !== null
            ? navigationSnapshot.applicants[pendingPosition]
            : undefined;
    const pendingLoadedApplicantId = getLoadedApplicantId(
        pendingApplicant,
        navigationInfo.fieldArray,
        canHydrateUnresolvedApplicants
    );
    const liveApplicantIndex = localApplicantId
        ? findApplicantIndex(navigationInfo.fieldArray, localApplicantId)
        : -1;
    const applicantIndex =
        liveApplicantIndex >= 0
            ? liveApplicantIndex
            : (currentApplicant?.virtualIndex ?? -1);
    const companyId = jobDataInsideJob?.jobData?.data?.companyId;

    React.useEffect(() => {
        setNavigationSnapshot((currentSnapshot) => {
            const applicants = hydrateApplicantNavigationSnapshot(
                currentSnapshot.applicants,
                navigationInfo.fieldArray,
                canHydrateUnresolvedApplicants
            );
            return applicants === currentSnapshot.applicants
                ? currentSnapshot
                : { ...currentSnapshot, applicants };
        });
    }, [
        canHydrateUnresolvedApplicants,
        loadedApplicantId,
        navigationInfo.fieldArray,
        pendingLoadedApplicantId
    ]);

    React.useEffect(() => {
        if (pendingPosition === null) {
            return;
        }
        if (!canHydrateUnresolvedApplicants) {
            setNavigationState((currentState) => ({
                ...currentState,
                pendingPosition: null
            }));
            return;
        }

        setNavigationState((currentState) =>
            resolveApplicantNavigationState(
                currentState,
                navigationSnapshot.applicants,
                pendingPosition
            )
        );
    }, [
        canHydrateUnresolvedApplicants,
        navigationSnapshot.applicants,
        pendingLoadedApplicantId,
        pendingPosition
    ]);

    React.useEffect(() => {
        if (open && !currentApplicant) {
            onClose();
        }
    }, [open, currentApplicant]);

    React.useEffect(() => {
        apiDirectCall("/customcv/getcustomcv", "post", {
            companyId,
            jobId: jobDataInsideJob?.jobId
        }).then((result: any) => {
            setCustomCVsData(result.data || []);
            setExternalDataLoadFinished(true);
        });
    }, [companyId, jobDataInsideJob]);

    const [t, i18n, readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });

    const isXSScreen = useMediaQuery(theme.breakpoints.only("xs"));
    const classPaperProps = !isXSScreen ? classes.paperDialogProps : "";

    const scrollToApplicant = (applicantPosition: number) => {
        const applicant = navigationSnapshot.applicants[applicantPosition];
        if (!applicant.applicantId && !canHydrateUnresolvedApplicants) {
            return;
        }
        const liveApplicantIndex = applicant.applicantId
            ? findApplicantIndex(
                  navigationInfo.fieldArray,
                  applicant.applicantId
              )
            : -1;
        const reactWindowCurrent: any = navigationInfo.reactWindowRef?.current;
        if (reactWindowCurrent) {
            reactWindowCurrent.scrollToItem(
                liveApplicantIndex >= 0
                    ? liveApplicantIndex
                    : applicant.virtualIndex
            );
        }
    };

    const previousPosition = findApplicantNavigationPosition(
        navigationSnapshot.applicants,
        currentPosition,
        -1,
        canHydrateUnresolvedApplicants
    );
    const nextPosition = findApplicantNavigationPosition(
        navigationSnapshot.applicants,
        currentPosition,
        1,
        canHydrateUnresolvedApplicants
    );

    const navigateToApplicant = (applicantPosition: number) => {
        if (applicantPosition >= 0) {
            setNavigationState((currentState) =>
                resolveApplicantNavigationState(
                    currentState,
                    navigationSnapshot.applicants,
                    applicantPosition
                )
            );
            scrollToApplicant(applicantPosition);
        }
    };

    useHotkeys(
        "ctrl+q,right,left",
        (event: any, handler: any) => {
            event.preventDefault();
            switch (handler.key) {
                case "ctrl+q":
                    onClose();
                    break;
                case "right":
                    navigateToApplicant(nextPosition);
                    break;
                case "left":
                    navigateToApplicant(previousPosition);
                    break;
                default:
                    break;
            }
        },
        {},
        [navigateToApplicant, nextPosition, onClose, previousPosition]
    );

    return (
        <Dialog
            fullWidth
            fullScreen={isXSScreen}
            disableBackdropClick
            disableEscapeKeyDown
            maxWidth="xl"
            aria-labelledby={t("ModalSortColumn_Title")}
            open={open}
            PaperProps={{ className: classPaperProps }}
        >
            <DialogContent style={{ padding: 0, margin: 0 }}>
                {open &&
                    localApplicantId &&
                    readyTranslation &&
                    externalDataLoadFinished && (
                        <ApplicantView
                            applicantId={localApplicantId}
                            containerType="modal"
                            index={applicantIndex}
                            jobDataInsideJob={jobDataInsideJob}
                            isInsideKanban={true}
                            module={module}
                            customCVsData={customCVsData}
                        />
                    )}
            </DialogContent>
            {open && readyTranslation && (
                <DialogActions className={classes.dialogActions}>
                    {previousPosition >= 0 && (
                        <Button
                            onClick={() => {
                                navigateToApplicant(previousPosition);
                            }}
                            color="primary"
                            variant="outlined"
                            startIcon={<NavigateBeforeIcon />}
                        >
                            {t("COMPLEOGENERAL_PREVIOUS")}
                        </Button>
                    )}
                    {nextPosition >= 0 && (
                        <Button
                            onClick={() => {
                                navigateToApplicant(nextPosition);
                            }}
                            color="primary"
                            variant="outlined"
                            endIcon={<NavigateNextIcon />}
                        >
                            {t("COMPLEOGENERAL_NEXT")}
                        </Button>
                    )}
                    <Button
                        onClick={() => {
                            onClose();
                        }}
                        color="secondary"
                        variant="contained"
                        startIcon={<CloseIcon />}
                    >
                        {t("COMPLEOGENERAL_CLOSE")}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ApplicantViewModalContainerKanban;
